import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import Table from '../../../components/Table';
import apiService from '../../../services/apiService';
import { FaEdit, FaEye, FaTrash } from 'react-icons/fa';

const BASE_URL = 'http://localhost:5000';

function PendingVerifications() {
  const { data: pending, loading: pendingLoading, fetchData: fetchPending } = useApi('/users/pending');
  const navigate = useNavigate();

  const handleViewDocument = async (userId, doc) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in.');
      }
      const response = await fetch(`${BASE_URL}/api/users/document/${userId}/${doc._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Document fetch error:', errorData);
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission to view this document.');
        } else {
          throw new Error(errorData.error || `Failed to access document (Status: ${response.status})`);
        }
      }
      // Handle document view logic if needed (e.g., open in a modal or new window)
    } catch (err) {
      console.error('handleViewDocument error:', err.message);
      toast.error(err.message);
    }
  };

  const handleDeleteDocument = async (userId, documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await apiService.delete(`/users/document/${userId}/${documentId}`);
      toast.success('Document deleted successfully');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete document');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiService.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const pendingColumns = [
    { 
      key: 'fullName', 
      label: 'Name', 
      render: (user) => `${user.firstName} ${user.lastName}` 
    },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    {
      key: 'documents',
      label: 'Documents',
      render: (user) => (
        <div className="flex space-x-2">
          {user.verificationDocuments.length > 0 ? (
            user.verificationDocuments.map(doc => (
              <div key={doc._id} className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewDocument(user._id, doc)}
                  className="text-blue-500 hover:text-blue-600"
                  title={`View ${doc.type}`}
                >
                  <FaEye />
                </button>
                <button
                  onClick={() => handleDeleteDocument(user._id, doc._id)}
                  className="text-red-500 hover:text-red-600"
                  title="Delete Document"
                >
                  <FaTrash />
                </button>
              </div>
            ))
          ) : 'None'}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/users/${user._id}`)}
            className="text-yellow-500 hover:text-yellow-600"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleDeleteUser(user._id)}
            className="text-red-500 hover:text-red-600"
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="">
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Pending Verifications</h2>
      {pendingLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">Loading...</div>
      ) : (
        <div className="card">
          <Table columns={pendingColumns} data={pending || []} />
        </div>
      )}
    </div>
  );
}

export default PendingVerifications;